/* eslint-disable i18next/no-literal-string -- DOM attribute/id names and diagnostics, not user-facing copy. */
import { useEffect } from 'react'

import { captureWarning } from '../components/Sentry/utils'

/**
 * Yotpo's identification contract (ULU-117): `#swell-customer-identification` marks the shopper as
 * logged-in. `data-token` is the server-signed SHA256(email + Yotpo API key) from the customer search.
 * @see https://support.yotpo.com/docs/implementing-loyalty-referrals-on-a-custom-or-generic-ecommerce-platform
 */
const IDENTIFICATION_ELEMENT_ID = 'swell-customer-identification'

export interface SwellCustomerIdentificationParams {
  /** Logged-in shopper email. */
  email?: string
  /** Yotpo external/customer id from the BFF customer search. */
  customerId?: string | null
  /** Server-signed Yotpo token; required for identification when `requiresToken` is true. */
  yotpoDataToken?: string | null
  /** Locale flag (`swellRequiresToken`) — when true Yotpo only trusts identification carrying a token. */
  requiresToken?: boolean
  /** Only inject for authenticated shoppers; anonymous visitors keep the normal flow. */
  enabled: boolean
}

/**
 * Injects (and cleans up) the `#swell-customer-identification` element so the Yotpo referral widget
 * recognizes a logged-in shopper. No-ops for anonymous visitors or until the customer id is known.
 */
export const useSwellCustomerIdentification = ({
  email,
  customerId,
  yotpoDataToken,
  requiresToken = false,
  enabled,
}: SwellCustomerIdentificationParams): void => {
  useEffect(() => {
    if (typeof document === 'undefined' || !enabled || !email || !customerId) return undefined

    // Replace any stale identification (e.g. a previous account on the same tab).
    document.getElementById(IDENTIFICATION_ELEMENT_ID)?.remove()

    const div = document.createElement('div')
    div.id = IDENTIFICATION_ELEMENT_ID
    div.setAttribute('data-authenticated', 'true')
    div.setAttribute('data-email', email)
    div.setAttribute('data-id', String(customerId))

    if (requiresToken) {
      if (yotpoDataToken) {
        div.setAttribute('data-token', yotpoDataToken)
      } else {
        captureWarning(
          'yotpoDataToken missing when Swell requires token',
          { hasCustomerId: Boolean(customerId), hasEmail: Boolean(email) },
          { source: 'useSwellCustomerIdentification' },
        )
      }
    }

    div.style.display = 'none'
    document.body.appendChild(div)

    return () => {
      document.getElementById(IDENTIFICATION_ELEMENT_ID)?.remove()
    }
  }, [enabled, email, customerId, yotpoDataToken, requiresToken])
}

export default useSwellCustomerIdentification
/* eslint-enable i18next/no-literal-string */
